import { createContext, useContext, useMemo, useReducer, useEffect, useRef } from "react"
import { omit, omitBy, pick, pickBy, isNil } from "lodash"
import { useDeleteTemplate, useSaveTemplate } from "resources/templates"
import { produce } from "immer"
import { usePreferences } from "hooks/use-preferences"
import { useReadContextPreference, useSaveContextPreferencesDual } from "hooks/preferences"
import { OK, ERROR } from "helpers/data-statuses"
import {
  getAssetViewPreferencePersistencePlan,
  getPreferenceSubTypeForPanel,
} from "./asset-view-preference-keys"

export const AssetViewContext = createContext()

const PREFERENCE_SUBTYPE_BASE = "asset-view"
// Placeholder when not duplicating from another panel; preference read hook requires a string key.
const DUPLICATE_NO_SOURCE_SUBTYPE = `${PREFERENCE_SUBTYPE_BASE}-duplicate-none`

const PREFERENCE_FIELDS = [
  "view",
  "groupBy",
  "selectedTableLevels",
  "showNotHeld",
  "columnOrder",
  "datagridState",
]

function hasOwnSavedPreferences(ownPreferenceStatus, ownPreferences) {
  if (ownPreferenceStatus !== OK || ownPreferences == null) return false
  const cleaned = pickBy(ownPreferences, (v) => v != null)
  return Object.keys(cleaned).length > 0
}

function useApplyDuplicatePreferences(
  sourcePreferenceSubType,
  sourcePreferenceStatus,
  sourcePreferences,
  ownPreferenceStatus,
  ownPreferences,
  dispatch,
  setPreferencesAction,
  initialView,
  panelId,
  state,
  savePreferences,
) {
  const hasAppliedRef = useRef(false)
  const ownHasFinishedLoading = ownPreferenceStatus === OK || ownPreferenceStatus === ERROR
  useEffect(() => {
    if (!sourcePreferenceSubType || sourcePreferenceStatus !== OK || !sourcePreferences || hasAppliedRef.current) return
    if (!ownHasFinishedLoading || hasOwnSavedPreferences(ownPreferenceStatus, ownPreferences)) return
    hasAppliedRef.current = true
    let cleaned = pickBy(sourcePreferences, (v) => v != null)
    if (initialView != null) {
      cleaned = omit(cleaned, ["view"])
    }
    if (Object.keys(cleaned).length === 0) {
      return
    }
    dispatch(setPreferencesAction(cleaned))
    // Save now so the first debounced save is not skipped with an empty payload (state not yet merged).
    const merged = { ...state, ...cleaned }
    const prefs = omitBy(pick(merged, PREFERENCE_FIELDS), isNil)
    if (Object.keys(prefs).length > 0 && panelId) {
      savePreferences(prefs)
    }
  }, [
    sourcePreferenceSubType,
    sourcePreferenceStatus,
    sourcePreferences,
    ownHasFinishedLoading,
    ownPreferenceStatus,
    ownPreferences,
    dispatch,
    setPreferencesAction,
    initialView,
    panelId,
    state,
    savePreferences,
  ])
}

const DEFAULT_STATE = {
  view: "summary",
  groupBy: null,
  selectedTableLevels: [],
  showMetricsSelector: false,
  columnOrder: "default",
  datagridState: {},
}

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setView":
        draft.view = action.view
        break
      case "setExpanded":
        draft.isExpanded = action.isExpanded
        break
      case "setGroupBy":
        draft.groupBy = action.groupBy
        // Reset selectedTableLevels when groupBy changes since the previous path may no longer be valid
        draft.selectedTableLevels = []
        break
      case "setShowNotHeld":
        draft.showNotHeld = action.showNotHeld
        break
      case "setShowMetricsSelector":
        draft.showMetricsSelector = action.showMetricsSelector
        break
      case "setSelectedTableLevels":
        draft.selectedTableLevels = action.selectedTableLevels
        break
      case "setColumnOrder":
        draft.columnOrder = action.columnOrder
        break
      case "setDatagridState":
        draft.datagridState = action.datagridState
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
    }
  })
}

function setView(view) {
  return {
    type: "setView",
    view,
  }
}

function setGroupBy(groupBy) {
  return {
    type: "setGroupBy",
    groupBy,
  }
}

function setSelectedTableLevels(selectedTableLevels) {
  return {
    type: "setSelectedTableLevels",
    selectedTableLevels,
  }
}

function setShowNotHeld(showNotHeld) {
  return {
    type: "setShowNotHeld",
    showNotHeld,
  }
}

function setShowMetricsSelector(showMetricsSelector) {
  return {
    type: "setShowMetricsSelector",
    showMetricsSelector,
  }
}

function setColumnOrder(columnOrder) {
  return {
    type: "setColumnOrder",
    columnOrder,
  }
}

function setDatagridState(datagridState) {
  return {
    type: "setDatagridState",
    datagridState,
  }
}

function setPreferences(preferences) {
  return {
    type: "setPreferences",
    preferences,
  }
}

export default function useAssetViewContext(parentContext, options = {}) {
  const { initialView, panelId, duplicateFromPanelId, isSecondary, shouldUseLegacyPreferences } = options
  const parentContextValue = useContext(parentContext)

  const {
    storesPreferencesUnderLegacyKey,
    preferencePersistenceSubType,
    mirrorSaveSubType,
  } = useMemo(
    () => getAssetViewPreferencePersistencePlan({
      panelId,
      duplicateFromPanelId,
      isSecondary,
      shouldUseLegacyPreferences,
    }),
    [panelId, duplicateFromPanelId, isSecondary, shouldUseLegacyPreferences],
  )

  const initialState = useMemo(() => ({
    ...DEFAULT_STATE,
    ...(initialView != null && {
      view: initialView,
    }),
  }), [initialView])
  const [state, dispatch] = useReducer(reducer, initialState)

  const savePreferences = useSaveContextPreferencesDual(
    preferencePersistenceSubType,
    mirrorSaveSubType,
  )

  const sourcePreferenceSubType = useMemo(
    () => (duplicateFromPanelId ? getPreferenceSubTypeForPanel(duplicateFromPanelId) : null),
    [duplicateFromPanelId],
  )
  const [sourcePreferences, sourcePreferenceStatus] = useReadContextPreference(
    sourcePreferenceSubType ?? DUPLICATE_NO_SOURCE_SUBTYPE,
    undefined,
    { enabled: !!sourcePreferenceSubType },
  )
  // Same stable key as usePreferences below; TanStack Query dedupes so only one request runs.
  const [ownPreferences, ownPreferenceStatus] = useReadContextPreference(
    preferencePersistenceSubType,
    undefined,
    { enabled: !!duplicateFromPanelId },
  )
  useApplyDuplicatePreferences(
    sourcePreferenceSubType,
    sourcePreferenceStatus,
    sourcePreferences,
    ownPreferenceStatus,
    ownPreferences,
    dispatch,
    setPreferences,
    initialView,
    panelId,
    state,
    savePreferences,
  )

  const isPreferencesLoaded = usePreferences(
    preferencePersistenceSubType,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
    {
      mirrorSaveSubType,
      ...(storesPreferencesUnderLegacyKey && initialView != null && { omitLoadedKeys: ["view"] }),
    },
  )

  const contextUpdaters = useMemo(() => ({
    setView: (...args) => dispatch(setView(...args)),
    setGroupBy: (...args) => dispatch(setGroupBy(...args)),
    setShowNotHeld: (...args) => dispatch(setShowNotHeld(...args)),
    setShowMetricsSelector: (...args) => dispatch(setShowMetricsSelector(...args)),
    setSelectedTableLevels: (...args) => dispatch(setSelectedTableLevels(...args)),
    setColumnOrder: (...args) => dispatch(setColumnOrder(...args)),
    setDatagridState: (...args) => dispatch(setDatagridState(...args)),
  }), [dispatch])

  const deleteTemplate = useDeleteTemplate()
  const saveTemplate = useSaveTemplate()

  const contextValue = useMemo(() => ({
    ...parentContextValue,
    ...state,
    ...contextUpdaters,
    deleteTemplate,
    saveTemplate,
    isPreferencesLoaded,
  }), [state, parentContextValue, contextUpdaters, deleteTemplate, saveTemplate, isPreferencesLoaded])

  return [AssetViewContext, contextValue]
}
